import { Navigate, Routes, Route } from 'react-router';
import { useEffect, lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import './main.css';
import 'streamdown/styles.css';

import { initializeAuth } from './stores/useAuthStore';

import PublicLayout from './routes/layout/PublicLayout';
import PrivateLayout from './routes/layout/PrivateLayout';
import GlobalSpinner from './components/GlobalSpinner';
import { DateProvider } from './contexts/DateContext';
import { SidebarProvider } from '~/components/ui/Sidebar';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '~/contexts/ThemeContext';
import GoogleBusinessProfile from './routes/connector/GoogleBusinessProfile';
import { queryClient } from './lib/queryClient';
import Amazon from './routes/connector/Amazon';

const Home = lazy(() => import('./routes/Home'));
const Up = lazy(() => import('./routes/up'));
const InvitedRegister = lazy(() => import('./routes/auth/InvitedRegister'));
const InactiveInvite = lazy(() => import('./routes/auth/InactiveInvite'));

// Auth routes
const Login = lazy(() => import('./routes/auth/Login'));
const Register = lazy(() => import('./routes/auth/Register'));
const RequestPasswordReset = lazy(
  () => import('./routes/auth/RequestPasswordReset'),
);
const ResetPassword = lazy(() => import('./routes/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./routes/auth/VerifyEmail'));

// Private routes
const Profile = lazy(() => import('./routes/Profile'));
const JoinTeam = lazy(() => import('./routes/team/JoinTeam'));

// Dashboard routes
const OverallDashboard = lazy(() => import('./routes/dashboard/Overall'));
const ChannelPerformance = lazy(
  () => import('./routes/dashboard/ChannelInsights'),
);
const PaidSocial = lazy(() => import('./routes/dashboard/PaidSocial'));
const PaidSearch = lazy(() => import('./routes/dashboard/PaidSearch'));
const OrganicSocial = lazy(() => import('./routes/dashboard/OrganicSocial'));
const OrganicSearch = lazy(() => import('./routes/dashboard/OrganicSearch'));
const Marketplace = lazy(() => import('./routes/dashboard/Marketplace'));
const BlinkitBrands = lazy(() => import('./routes/dashboard/BlinkitBrands'));
const StorePerformance = lazy(
  () => import('./routes/dashboard/StorePerformance'),
);
const QCommShelf = lazy(() => import('./routes/dashboard/QCommShelf'));
const QCommTrends = lazy(() => import('./routes/dashboard/QCommTrends'));
const AppAnalytics = lazy(() => import('./routes/dashboard/AppAnalytics'));
const Sales = lazy(() => import('./routes/dashboard/Sales'));
const Competitor = lazy(() => import('./routes/dashboard/Competitor'));
const LeadsTracking = lazy(() => import('./routes/dashboard/LeadsTracking'));
const PerformanceTracking = lazy(
  () => import('./routes/dashboard/PerformanceTracking'),
);

// Team routes
const TeamManage = lazy(() => import('./routes/team/Manage'));
const BrandProfile = lazy(() => import('./routes/team/BrandProfile'));
const Connections = lazy(() => import('./routes/team/Connections'));
const LocationManagement = lazy(
  () => import('./routes/team/LocationManagement'),
);

// Connector routes
const GoogleAds = lazy(() => import('./routes/connector/GoogleAds'));
const GoogleAnalytics = lazy(
  () => import('./routes/connector/GoogleAnalytics'),
);
const SearchConsole = lazy(() => import('./routes/connector/SearchConsole'));
const YouTubeAnalytics = lazy(
  () => import('./routes/connector/YouTubeAnalytics'),
);
const MetaAds = lazy(() => import('./routes/connector/MetaAds'));
const Instagram = lazy(() => import('./routes/connector/Instagram'));
const LinkedinAds = lazy(() => import('./routes/connector/LinkedinAds'));
const TikTok = lazy(() => import('./routes/connector/TikTok'));
const LinkedinPages = lazy(() => import('./routes/connector/LinkedinPages'));
const AmazonAds = lazy(() => import('./routes/connector/AmazonAds'));
const Hubspot = lazy(() => import('./routes/connector/Hubspot'));
const Salesforce = lazy(() => import('./routes/connector/Salesforce'));
const Blinkit = lazy(() => import('./routes/connector/Blinkit'));
const BlinkitBrandsConnector = lazy(
  () => import('./routes/connector/BlinkitBrands'),
);
const Zepto = lazy(() => import('./routes/connector/Zepto'));
const Instamart = lazy(() => import('./routes/connector/Instamart'));
const Bigbasket = lazy(() => import('./routes/connector/Bigbasket'));
const Flipkart = lazy(() => import('./routes/connector/Flipkart'));
const Leadsquared = lazy(() => import('./routes/connector/Leadsquared'));
const FacebookPage = lazy(() => import('./routes/connector/FacebookPage'));
const AppsFlyer = lazy(() => import('./routes/connector/AppsFlyer'));
const VMart = lazy(() => import('./routes/connector/VMart'));
// Chat routes
const NewConversation = lazy(() => import('./routes/chat/NewConversation'));
const ExistingConversation = lazy(
  () => import('./routes/chat/ExistingConversation'),
);

// 404 route
const NotFound = lazy(() => import('./routes/NotFound'));

function App() {
  useEffect(() => {
    initializeAuth();

    if (
      import.meta.env.MODE === 'production' &&
      import.meta.env.VITE_CLARITY_ID
    ) {
      import('@microsoft/clarity').then((Clarity) => {
        Clarity.default.init(import.meta.env.VITE_CLARITY_ID);
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <SidebarProvider>
        <DateProvider>
          <QueryClientProvider client={queryClient}>
            <Suspense fallback={<GlobalSpinner />}>
              <Routes>
                <Route index element={<Home />} />
                <Route path="up" element={<Up />} />
                <Route
                  path="teams/:teamId/invitations/:invitationToken"
                  element={<InvitedRegister />}
                />
                <Route
                  path="team-invitations/:teamId/:invitationToken"
                  element={<InactiveInvite />}
                />

                <Route path="/" element={<PublicLayout />}>
                  <Route path="login" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route
                    path="forgot-password"
                    element={<RequestPasswordReset />}
                  />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="verify" element={<VerifyEmail />} />
                </Route>

                <Route path="/" element={<PrivateLayout />}>
                  <Route path="profile" element={<Profile />} />
                  <Route
                    path="join-team/:teamId/invitations/:invitationToken/:email"
                    element={<JoinTeam />}
                  />

                  <Route path="dashboard">
                    <Route index element={<OverallDashboard />} />
                    <Route
                      path="channel-performance"
                      element={<ChannelPerformance />}
                    />
                    <Route path="paid-social" element={<PaidSocial />} />
                    <Route path="paid-search" element={<PaidSearch />} />
                    <Route path="organic-social" element={<OrganicSocial />} />
                    <Route path="organic-search" element={<OrganicSearch />} />
                    <Route path="marketplace" element={<Marketplace />} />
                    <Route path="blinkit-brands" element={<BlinkitBrands />} />
                    <Route
                      path="store-performance"
                      element={<StorePerformance />}
                    />
                    <Route path="qcomm-shelf" element={<QCommShelf />} />
                    <Route path="qcomm-trends" element={<QCommTrends />} />
                    <Route
                      path="blinkit-scraper"
                      element={<Navigate to="/dashboard/qcomm-shelf" replace />}
                    />
                    <Route path="app-analytics" element={<AppAnalytics />} />
                    <Route path="sales" element={<Sales />} />
                    <Route path="competitor" element={<Competitor />} />
                    <Route path="leads-tracking" element={<LeadsTracking />} />
                    <Route
                      path="performance-tracking"
                      element={<PerformanceTracking />}
                    />
                  </Route>

                  <Route path="team">
                    <Route path="manage" element={<TeamManage />} />
                    <Route path="brand-profile" element={<BrandProfile />} />
                    <Route path="connections" element={<Connections />} />
                    <Route path="locations" element={<LocationManagement />} />
                  </Route>

                  <Route path="connector">
                    <Route path="google-ads" element={<GoogleAds />} />
                    <Route
                      path="google-analytics-data-api"
                      element={<GoogleAnalytics />}
                    />
                    <Route
                      path="google-search-console"
                      element={<SearchConsole />}
                    />
                    <Route
                      path="google-business-profile"
                      element={<GoogleBusinessProfile />}
                    />
                    <Route
                      path="youtube-analytics"
                      element={<YouTubeAnalytics />}
                    />
                    <Route path="facebook-marketing" element={<MetaAds />} />
                    <Route path="instagram" element={<Instagram />} />
                    <Route path="linkedin-ads" element={<LinkedinAds />} />
                    <Route path="tiktok-marketing" element={<TikTok />} />
                    <Route path="linkedin-pages" element={<LinkedinPages />} />
                    <Route path="amazon-ads" element={<AmazonAds />} />
                    <Route path="hubspot" element={<Hubspot />} />
                    <Route path="salesforce" element={<Salesforce />} />
                    <Route path="blinkit" element={<Blinkit />} />
                    <Route
                      path="blinkit-brands"
                      element={<BlinkitBrandsConnector />}
                    />
                    <Route path="zepto" element={<Zepto />} />
                    <Route path="instamart" element={<Instamart />} />
                    <Route path="bigbasket" element={<Bigbasket />} />
                    <Route path="flipkart" element={<Flipkart />} />
                    <Route path="amazon" element={<Amazon />} />
                    <Route path="leadsquared" element={<Leadsquared />} />
                    <Route path="facebook-pages" element={<FacebookPage />} />
                    <Route path="appsflyer" element={<AppsFlyer />} />
                    <Route path="vmart" element={<VMart />} />
                  </Route>
                  <Route path="chat">
                    <Route index element={<NewConversation />} />
                    <Route path=":chatId" element={<ExistingConversation />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </QueryClientProvider>
        </DateProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="max-w-xl w-full shadow-md rounded-lg p-6 space-y-4">
        <div className="flex items-start space-x-4">
          <div className="text-4xl">⚠️</div>
          <div className="space-y-2">
            <h1 className="text-xl font-bold">Something went wrong</h1>
            <p className="text-sm">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred.'}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={resetError}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  ),
});
